import { SupportTicketType } from 'src/app/types/support';
import { ErrorService } from 'src/app/services/general/error.service';
import { ActivatedRoute, NavigationExtras, Params, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { UserService } from 'src/app/services/user/user.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ModalController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { InvisibleReCaptchaComponent } from 'ngx-captcha';

import { AccountsListService } from 'src/app/services/accounts/accounts-list.service';
import { OnlineService } from 'src/app/services/general/online.service';
import { UpdateByService } from 'src/app/services/user/update-by.service';
import { LanguageService } from 'src/app/services/general/language.service';
import { PopupService } from 'src/app/services/general/popup.service';

import { User } from 'src/app/interfaces/user';
import { SupportTicketing } from 'src/app/interfaces/support';

import { AccountsListComponent } from 'src/app/components/accounts/accounts-list/accounts-list.component';
import { environment } from 'src/environments/environment';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { LinkService } from 'src/app/services/general/link.service';
import { PhotoService } from 'src/app/services/general/photo.service';
import { KeyboardService } from 'src/app/services/general/keyboard.service';

@Component({
  selector: 'app-support',
  templateUrl: './support.page.html',
  styleUrls: ['./support.page.scss'],
})
export class SupportPage implements OnInit, OnDestroy {
  /**
   * Captcha viewchild
   */
  @ViewChild('captchaElem', { static: false }) captchaElem: InvisibleReCaptchaComponent;

  ready: boolean;

  ticketType: SupportTicketType;

  step: number;
  /**
   * User Subscription
   */
  private userSubscription: Subscription;

  supportForm: FormGroup;
  /**
   * Validation msg
   */
  validationMsg: any;

  /**
   * Recaptcha site key
   */
  siteKey: string = environment.recaptcha.invisible;
  /**
   * Recaptcha flag
   */
  recaptcha: boolean;
  /**
   * Recaptcha model
   */
  recaptchaModel: any;
  /**
   * Recaptcha Response
   */
  private recaptchaResponse: string;

  private langCode: string;

  backHelpPage: boolean;
  backRatingPage: boolean;

  constructor(
    private platform: Platform,
    private route: ActivatedRoute,
    private router: Router,
    private fns: AngularFireFunctions,
    private formBuilder: FormBuilder,
    private modalController: ModalController,
    private translate: TranslateService,
    private accountsListService: AccountsListService,
    private userService: UserService,
    private updateByService: UpdateByService,
    private languageService: LanguageService,
    private photoService: PhotoService,
    private onlineService: OnlineService,
    private linkService: LinkService,
    private popupService: PopupService,
    private errorService: ErrorService,
    private keyboardService: KeyboardService,
  ) {
  }

  ngOnInit() {
    this.setupForm();
    this.watchRoute();
  }

  ngOnDestroy(): void {
      this.unwatchUser();
  }

  ionViewWillEnter() {
    this.ready = true;
    this.step = 1;
    this.recaptcha = true;
    this.setupLanguage();
    this.watchUser();
    this.photoService.supportAttachementList = [];
  }

  ionViewWillLeave() {
    this.ready = false;
    this.recaptcha = false;
    this.unwatchUser();
  }

  /**
   * Watch Route
   */
  watchRoute() {
    this.route.params.subscribe((param: Params) => {
      const route = this.router.getCurrentNavigation();
      const state = route?.extras?.state;
      if (param.ticketType) {
        this.setupTicketType(param.ticketType);
      } else if (state?.ticketType) {
        this.setupTicketType(route.extras.state.ticketType);
      } else {
        this.setupTicketType('enquiry');
      }

      if (param.backHelpPage) {
        this.backHelpPage = true;
      } else if (state?.backHelpPage) {
        this.backHelpPage = true;
      } else {
        this.backHelpPage = false;
      }

      if (param.backRatingPage) {
        this.backRatingPage = true;
      } else if (state?.backRatingPage) {
        this.backRatingPage = true;
      } else {
        this.backRatingPage = false;
      }
    });
  }

  async setupLanguage() {
    await this.platform.ready();
    if (this.userService?.uid) {
      this.langCode = this.languageService.getUserLanguage()?.code;
    } else {
      this.langCode = (await this.languageService.readDeviceLanguage())?.code;
    }
  }

  /**
   * Setup form
   */
  async setupForm() {
    this.validationMsg = {
      accountId: [{ type: 'required', msg: this.translate.instant('VALIDATION.required', { field: this.translate.instant('MODULE.list.account') }) }],
      type: [{ type: 'required', msg: this.translate.instant('VALIDATION.required', { field: this.translate.instant('LBL.type') }) }],
      name: [{ type: 'required', msg: this.translate.instant('VALIDATION.required', { field: this.translate.instant('LBL.name') }) }],
      email: [{ type: 'required', msg: this.translate.instant('VALIDATION.required', { field: this.translate.instant('LBL.email') }) },
        { type: 'email', msg: this.translate.instant('VALIDATION.invalid_format', { field: this.translate.instant('LBL.email') }) }],
      msg: [{ type: 'required', msg: this.translate.instant('VALIDATION.required', { field: this.translate.instant('LBL.msg') }) }],
    };

    this.supportForm = this.formBuilder.group({
      type: new FormControl('', [ Validators.required ]),
      accountId: new FormControl('', []),
      name: new FormControl(this.userService.user?.name, [ Validators.required ]),
      email: new FormControl('', [ Validators.required, Validators.email ]),
      msg: new FormControl('', [ Validators.required ])
    });
  }

  setupTicketType(ticketType: SupportTicketType) {
    this.ticketType = ticketType;
    if (this.supportForm) {
      this.supportForm.controls.type.setValue(ticketType);
      if (ticketType === 'refund' || ticketType === 'aup_appeal') {
        this.updateAccountIdValidation(ticketType);
      }
    } else {
      setTimeout(()  => {
        this.setupTicketType(ticketType);
      }, 300);
    }
  }

  /**
   * Watch user
   */
  async watchUser() {
    if (!this.userSubscription) {
      this.userSubscription = this.userService.observableUser.subscribe((user: User) => {
        this.setName(user?.name);
        this.setEmail(user?.email);
      });
    }
    
  }

  /**
   * Unwatch user
   */
  async unwatchUser() {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
      this.userSubscription = null;
    }
  }

  setName(name?: string) {
    if (name && !this.supportForm?.value?.name) {
      this.supportForm.controls.name.setValue(name);
    }
  }

  setEmail(email?: string) {
    if (email && !this.supportForm?.value?.email) {
      this.supportForm.controls.email.setValue(email);
    }
  }

  segmentChanged(event: any) {
    if (event?.detail?.value) {
      this.updateAccountIdValidation(event.detail.value);
    }
  }

  updateAccountIdValidation(ticketType: SupportTicketType) {
    this.ticketType = ticketType;
    if (ticketType === 'refund' || ticketType === 'aup_appeal') {
      this.supportForm.controls.accountId.setValidators([ Validators.required ]);
    } else {
      this.supportForm.controls.accountId.clearValidators();
    }
    this.supportForm.controls.accountId.updateValueAndValidity();
  }

  async presentAccountsListModal() {
    const modal = await this.modalController.create({
      component: AccountsListComponent,
      componentProps: {
        selected: this.supportForm.value.accountId,
        supportTicktType: this.supportForm.value.type
      }
    });
    modal.present();
    modal.onWillDismiss().then((result: any) => {
      if (result?.data?.selected) {
        if (this.supportForm.value.accountId !== result.data.selected) {
          this.supportForm.controls.accountId.setValue(result.data.selected);
        }
      }
    });
  }

  getAccountTitle(accountId: string) {
    return this.accountsListService.getAccountTitle(accountId);
  }

  getSupportAttachmentList(): string[] {
    return this.photoService.supportAttachementList;
  }

  generateSupportTicketData(): SupportTicketing {
    const createBy = this.updateByService.getUpdateBy();
    const supportTicket: SupportTicketing = {
      ticketId: '',
      type: this.supportForm.value?.type,
      name: this.supportForm.value.name,
      msg: this.supportForm.value.msg,
      attachment: this.photoService.supportAttachementList,
      accountId: this.supportForm.value.accountId,
      email: this.supportForm.value.email,
      language: this.langCode,
      status: 'in_progress',
      createBy,
      updateBy: createBy
    };
    return supportTicket;
  }

  /**
   * Go main page
   */
  goMainPage() {
    this.linkService.goMainPage();
  }

  goContactPage() {
    const navigationExtras: NavigationExtras = {
      state: {
        backRatingPage: this.backRatingPage,
        backHelpPage: this.backHelpPage,
      }
    };
    this.router.navigate(['/about/contact'], navigationExtras);
  }

  async supportFormSubmit() {
    this.supportForm.markAllAsTouched();
    if (this.supportForm.valid && this.onlineService.isOnline()) {
      await this.popupService.presentLoading();
      this.captchaElem.execute();
    }
  }

  /**
   * Handle recaptcha reset
   */
   handleReset() {
  }

  /**
   * Handle recaptcha ready
   */
  handleReady() {
  }

  /**
   * Handle recaptcha load
   */
  handleLoad() {
  }

  /**
   * Handle recaptcha expire
   */
  handleExpire() {
    this.popupService.dismissLoading();
  }

  /**
   * Handle recaptcha success
   * @param event event
   */
  async handleSuccess(event: any) {
    this.recaptchaResponse = event;
    if (this.recaptchaResponse && this.recaptchaResponse === this.captchaElem.getCurrentResponse()) {
      await this.supportTicketCall(this.generateSupportTicketData());
      this.resetRecaptcha();
    } else {
      this.popupService.dismissLoading();
    }
  }

  /**
   * Handle recaptcha error
   */
  handleError() {
    this.popupService.dismissLoading();
  }

  /**
   * Reset recaptcha
   */
  resetRecaptcha() {
    if (this.captchaElem && this.recaptchaResponse) {
      this.recaptchaResponse = null;
      this.captchaElem.resetCaptcha();
    }
  }

  async selectPhoto() {
    this.photoService.selectPhoto('support_attachment', true);
  }

  async deletePhoto(index: number) {
    this.photoService.supportAttachementList.splice(index, 1);
  }

  async supportTicketCall(supportTicket: SupportTicketing) {
    if (this.onlineService.isOnline() && supportTicket) {
      await this.popupService.presentLoading();
      this.fns.httpsCallable('supportTicketCall')({ supportTicket }).toPromise().then(async (result) => {
        this.popupService.dismissLoading();
        if (result) {
          this.popupService.presentToast(this.translate.instant('MSG.action_done'), 'success');
          this.step = 2;
          this.supportForm.reset();
        } else {
          this.popupService.presentActionError();
        }
      }).catch((err: any) => {
        this.popupService.dismissLoading();
        this.popupService.presentActionError();
        this.errorService.logError(err);
      });
    }
  }

}
